import { z } from "zod";

/**
 * Which legs of the transport chain to list.
 *
 * `open` (the default) is the worklist: everything raised and not yet delivered, which is what a
 * driver with a handheld is looking at. The other scopes are the office's — `all` for the whole
 * history, `mine` for the legs this caller is currently carrying, which is the one list that
 * answers "what am I holding" after a handheld is put down and picked up again.
 *
 * `put-away` is the inbound half of `open`: the legs walking returned material from the goods-in
 * door to a shelf. It is a scope of its own rather than a zone filter because the two directions
 * are different work done by different people — a driver fetching boxes for a lorry and a
 * warehouseman putting a returned pallet away are not looking at one list, and the receiving zone
 * has no `followUpZoneId`, so it is absent from the zone pages the outbound screen is built from.
 */
export const getTransportOrdersQuerySchema = z.object({
	scope: z.enum(["open", "all", "mine", "put-away"]).optional(),
	/** Only legs starting in this zone — the shelf a driver is standing at. */
	fromZoneId: z.uuid().optional(),
	/** Only legs of this suborder, which is how a round's transport is read off its own page. */
	suborderId: z.uuid().optional(),
	/**
	 * Only legs this trolley carried, which is how a transfer carrier's own page reads its history.
	 *
	 * Unlike the scopes above it does **not** narrow to open legs: a carrier's page is asking what
	 * it has done, not what is waiting. Combine it with `scope: "put-away"` for the open ones.
	 */
	transferCarrierId: z.uuid().optional(),
});

export type GetTransportOrdersQuerySchema = z.infer<typeof getTransportOrdersQuerySchema>;
